import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { 
  MdDashboard, 
  MdPeople, 
  MdCheckCircle, 
  MdNoteAlt, 
  MdMenuBook, 
  MdSchool, 
  MdGroup, 
  MdCalendarToday, 
  MdSettings,
  MdExpandMore,
  MdAssessment,
  MdNotifications,
} from 'react-icons/md';
import AuthService from '../services/authService';
import axiosInstance from '../utils/axiosInstance';

const Aside = styled.aside`
  position: fixed;
  inset: 0 auto 0 0;
  z-index: 50;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #e5e7eb; /* gray-200 */
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);
  transition: width 0.3s ease;
  width: ${(p) => (p.$open ? '256px' : '64px')};
`;

const TopBar = styled.div`
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #e5e7eb;
  padding: 0 16px;
`;

const BrandBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BrandBadge = styled.div`
  height: 32px;
  width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: #4f46e5; /* indigo-600 */
  color: #fff;
  font-weight: 700;
  font-size: 12px;
`;

const Nav = styled.nav`
  flex: 1;
  padding: 12px 8px;
  overflow-y: auto;
`;

const NavButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: none;
  background: ${(p) => (p.$active ? '#4f46e5' : 'transparent')};
  color: ${(p) => (p.$active ? '#ffffff' : '#374151')};
  border-radius: 12px;
  padding: 10px 12px;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
  &:hover { background: ${(p) => (p.$active ? '#4338ca' : '#f3f4f6')}; }
`;

const NavLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  border-radius: ${(p) => (p.$isChild ? '8px' : '12px')};
  padding: ${(p) => (p.$isChild ? '8px 12px' : '10px 12px')};
  color: ${(p) => {
    if (p.$isChild && p.$active) return '#4f46e5';
    if (p.$active) return '#fff';
    return '#374151';
  }};
  background: ${(p) => {
    if (p.$isChild && p.$active) return '#eef2ff';
    if (p.$active) return '#4f46e5';
    return 'transparent';
  }};
  border-left: ${(p) => (p.$isChild && p.$active ? '3px solid #4f46e5' : '3px solid transparent')};
  text-decoration: none;
  font-size: ${(p) => (p.$isChild ? '13px' : '14px')};
  font-weight: ${(p) => (p.$active ? '600' : '400')};
  transition: all 0.2s;
  
  &:hover {
    background: ${(p) => {
      if (p.$isChild && p.$active) return '#e0e7ff';
      if (p.$active) return '#4338ca';
      if (p.$isChild) return '#f9fafb';
      return '#f3f4f6';
    }};
    color: ${(p) => (p.$isChild && !p.$active ? '#4f46e5' : p.$active && !p.$isChild ? '#fff' : '#374151')};
  }
  
  svg {
    font-size: 20px;
    flex-shrink: 0;
  }
`;

const ChildWrap = styled.div`
  margin-top: 6px;
  padding-left: 12px;
  display: ${(p) => (p.$open ? 'block' : 'none')};
`;

const Footer = styled.div`
  border-top: 1px solid #e5e7eb;
  padding: 16px;
  color: #6b7280; /* gray-500 */
  font-size: 12px;
`;

// Icon mapping
const getIcon = (name) => {
  const iconMap = {
    dashboard: <MdDashboard />,
    students: <MdPeople />,
    correction: <MdCheckCircle />,
    notepad: <MdNoteAlt />,
    report: <MdAssessment />,
    notifications: <MdNotifications />,
    courses: <MdMenuBook />,
    academic: <MdSchool />,
    staff: <MdGroup />,
    schedule: <MdCalendarToday />,
    settings: <MdSettings />,
  };
  return iconMap[name] || <MdDashboard />;
};

export default function Sidebar({ isOpen }) {
  const [expanded, setExpanded] = useState([]);

  const user = AuthService.getUser();
  const userRole = user?.superAdmin?.staff?.role || user?.superAdmin?.role;
  const userType = user?.superAdmin ? 'super_admin' : user?.type;
  const isSuperAdmin = Boolean(user?.superAdmin?.superAdminId);
  const isAdmin = isSuperAdmin || userRole === 'Admin' || userRole === 'Administrator';

  // Show Notepad section for all logged-in admin-frontend users
  const showNotepad = Boolean(user);
  const [hasHndCourses, setHasHndCourses] = useState(false);
  const [hasTopupCourses, setHasTopupCourses] = useState(false);
  const [canAccessBatchGroups, setCanAccessBatchGroups] = useState(false);

  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';

  useEffect(() => {
    let cancelled = false;

    const loadAccessFlags = async () => {
      try {
        const resp = await axiosInstance.get('/admin/me/coordinate-courses');
        if (cancelled || !resp?.data) return;
        const data = resp.data;
        setHasHndCourses(Boolean(data.hasHndCourses));
        setHasTopupCourses(Boolean(data.hasTopupCourses));

        const coordinateCourses = data.coordinateCourses || {};

        // Mirror the HND (IT) detection used by the Delivery backend.
        const HND_NAMES = [
          'Level 5 Pearson BTEC HND in Computing (General)',
          'Level 5 Pearson BTEC HND in Computing (Software Engineering)',
          'Level 5 Pearson BTEC HND in Computing (Network Engineering)',
        ].map((v) => v.toLowerCase());

        const HND_LIKE_GROUPS = [
          ['hnd', 'computing'],
        ];

        const coordinateCourseList = Object.values(coordinateCourses || {});

        const hasHndItCourse = coordinateCourseList.some((course) => {
          if (!course) return false;
          const fullNameLower = String(course.fullName || course.courseName || '').toLowerCase().trim();
          const shortNameLower = String(course.shortName || course.courseShortName || '').toLowerCase().trim();

          // First, try exact / contains match against configured HND Computing names
          const nameMatch = HND_NAMES.some((target) => {
            if (!target) return false;
            return (
              fullNameLower === target ||
              fullNameLower.includes(target) ||
              shortNameLower === target ||
              shortNameLower.includes(target)
            );
          });
          if (nameMatch) return true;

          // Fallback LIKE-style groups: all tokens must appear in either full or short name
          const fields = [fullNameLower, shortNameLower];
          const likeMatch = HND_LIKE_GROUPS.some((tokens) =>
            tokens.every((token) => {
              const lower = token.toLowerCase();
              return fields.some((field) => field.includes(lower));
            })
          );

          return likeMatch;
        });

        setCanAccessBatchGroups(Boolean(data.isSuperAdmin) || hasHndItCourse);
      } catch (err) {
        if (!cancelled) {
          setHasHndCourses(false);
          setHasTopupCourses(false);
          setCanAccessBatchGroups(false);
        }
      }
    };

    loadAccessFlags();

    return () => {
      cancelled = true;
    };
  }, []);

  const navigation = useMemo(() => ([
    { name: 'Dashboard', href: '/admin/dashboard', icon: getIcon('dashboard') },
    {
      name: 'Students', href: '#', icon: getIcon('students'),
      children: [
        { name: 'All Students', href: '/admin/students' },
        { name: 'My Course Due', href: '/admin/my-course-due' },
        { name: 'Inactivated Inquiry', href: '/admin/inactivated-inquiry' },
        { name: 'Inactivated List Followup', href: '/admin/inactivated-inquiry/by-assignee' },
        ...(isAdmin ? [{ name: 'Students Without Registration', href: '/admin/students-without-registration' }] : []),
      ],
    },
    ...(isSuperAdmin ? [{ name: 'Correction', href: '/admin/correction', icon: getIcon('correction') }] : []),
    ...(showNotepad ? [{ name: 'Notepad', href: '#', icon: getIcon('notepad'), children: [
      { name: 'To Do List', href: '/admin/todo-list' },
      { name: 'Student Issue', href: '/admin/student-issue' },
      ...(isAdmin ? [
        { name: 'Remider', href: '/admin/remider' },
        { name: 'Sticky Note', href: '/admin/sticky-note' },
      ] : []),
      ...(isAdmin ? [{ name: 'Contacts', href: '/admin/contacts' }] : []),
      ...(isAdmin ? [{ name: 'Problems', href: '/admin/problems' }] : []),
    ]}] : []),
    { name: 'Courses', href: '#', icon: getIcon('courses'), children: [
      { name: 'All Courses', href: '/admin/courses' },
    ]},
    { name: 'Academic', href: '#', icon: getIcon('academic'), children: [
      { name: 'Delivery', href: '/admin/delivery' },
      { name: 'My Exam', href: '/admin/my-exam' },
      { name: 'Academic Calendar', href: '/admin/academic-calendar' },
      ...(hasHndCourses ? [{ name: 'HND Exam Board', href: '/admin/hnd-exam-board' }] : []),
      ...(hasTopupCourses ? [{ name: 'Topup Exam Board', href: '/admin/topup-exam-board' }] : []),
      { name: 'Not Corrected Assignment List', href: '/admin/not-corrected-assignment-list' },
      ...(hasHndCourses ? [{ name: 'HND Assignment Tracking', href: '/admin/hnd-assignment-tracking' }] : []),
      ...(hasTopupCourses ? [{ name: 'TOPUP Assignment Tracking', href: '/admin/topup-assignment-tracking' }] : []),
    ]},
    { name: 'Staff', href: '#', icon: getIcon('staff'), children: [
      { name: 'All Staff', href: '/admin/staff' },
      ...(isAdmin ? [{ name: 'Due Course Mapping', href: '/admin/due-course-mapping' }] : []),
      ...(isSuperAdmin ? [{ name: 'Course Mapping', href: '/admin/course-mapping' }] : []),
      ...(isAdmin ? [{ name: 'Staff Unavailability', href: '/admin/staff-unavailability' }] : []),
    ]},
    { name: 'Schedule', href: '#', icon: getIcon('schedule'), children: [
      { name: 'Timetable', href: '/admin/schedule' },
      { name: 'Attendance Tracking', href: '/admin/attendance-tracking' },
      ...(isAdmin ? [{ name: 'Lecture Hours', href: '/admin/lecture-hours' }] : []),
      { name: 'Students Attendance', href: '/admin/students-attendance' },
    ]},
    ...(isAdmin ? [{
      name: 'Report',
      href: '#',
      icon: getIcon('report'),
      children: [
        { name: 'Student Detail', href: '/admin/report/student-detail' },
      ],
    }] : []),
    ...(isAdmin ? [{
      name: 'Notifications',
      href: '/admin/notifications',
      icon: getIcon('notifications'),
    }] : []),
    ...(() => {
      const settingsChildren = [];

      if (isSuperAdmin) {
        settingsChildren.push(
          { name: 'Positions', href: '/admin/positions' },
          { name: 'Courses', href: '/admin/courses' },
          { name: 'Subjects', href: '/admin/subjects' },
          { name: 'Assign Subject', href: '/admin/assign-subject' },
          { name: 'Classroom', href: '/admin/classroom' },
          { name: 'Batch', href: '/admin/batches' },
          { name: 'Batch Groups', href: '/admin/batch-groups' },
          { name: 'ELMS Module Code', href: '/admin/elms-module-code' },
        );
      } else if (canAccessBatchGroups) {
        // Non-superadmin coordinators with HND-IT access: only Batch + Batch Groups
        settingsChildren.push(
          { name: 'Batch', href: '/admin/batches' },
          { name: 'Batch Groups', href: '/admin/batch-groups' },
        );
      } else {
        // Everyone else keeps only the Batch option
        settingsChildren.push({ name: 'Batch', href: '/admin/batches' });
      }

      if (!settingsChildren.length) {
        return [];
      }

      return [{ name: 'Settings', href: '#', icon: getIcon('settings'), children: settingsChildren }];
    })(),
  ]), [isAdmin, isSuperAdmin, showNotepad, hasHndCourses, hasTopupCourses, canAccessBatchGroups]);

  useEffect(() => {
    const activeParents = [];
    navigation.forEach((item) => {
      if (item.children && item.children.some((child) => isActive(child.href))) {
        activeParents.push(item.name);
      }
    });
    if (activeParents.length) {
      setExpanded((prev) => Array.from(new Set([...prev, ...activeParents])));
    }
  }, [currentPath, navigation]);

  const isActive = (href) => {
    if (href === '/admin/dashboard') return currentPath === href;
    return currentPath.startsWith(href) && href !== '#';
  };

  const toggleExpanded = (name) => {
    setExpanded((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));
  };

  return (
    <Aside $open={isOpen}>
      <TopBar>
        {isOpen ? (
          <BrandBox>
            <BrandBadge>KA</BrandBadge>
            <span style={{ fontWeight: 700, fontSize: 16, color: '#111827' }}>Kaaryaalaya</span>
          </BrandBox>
        ) : (
          <BrandBadge>KA</BrandBadge>
        )}
      </TopBar>
      <Nav>
        {navigation.map((item) => {
          const hasChildren = !!(item.children && item.children.length);
          const itemActive = isActive(item.href) || (item.children && item.children.some((c) => isActive(c.href)));
          if (hasChildren) {
            const isExpanded = expanded.includes(item.name);
            return (
              <div key={item.name} style={{ marginBottom: 6 }}>
                <NavButton onClick={() => toggleExpanded(item.name)} $active={itemActive}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span>{item.icon}</span>
                    {isOpen && <span>{item.name}</span>}
                  </span>
                  {isOpen && (
                    <MdExpandMore style={{ fontSize: 18, transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                  )}
                </NavButton>
                {isOpen && (
                  <ChildWrap $open={isExpanded}>
                    {item.children.map((child) => {
                      const childActive = isActive(child.href);
                      return (
                        <div key={child.name} style={{ marginBottom: 4 }}>
                          <NavLink to={child.href} $active={childActive} $isChild={true}>
                            <span>{child.name}</span>
                          </NavLink>
                        </div>
                      );
                    })}
                  </ChildWrap>
                )}
              </div>
            );
          }
          return (
              <div key={item.name} style={{ marginBottom: 8 }}>
              <NavLink to={item.href} $active={itemActive}>
                <span>{item.icon}</span>
                {isOpen && <span>{item.name}</span>}
              </NavLink>
            </div>
          );
        })}
      </Nav>
      {isOpen && (
        <Footer>
          <div>
            <div style={{ fontWeight: 600 }}>Kaaryaalaya Analysis</div>
            <div style={{ marginTop: 4 }}>Version 1.0.0</div>
          </div>
        </Footer>
      )}
    </Aside>
  );
}
